import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, TrendingUp, Award, Gift } from "lucide-react";

const TIER_COLORS = {
  Explorer: { bg: "from-slate-500 to-slate-700", chip: "bg-slate-100 text-slate-700" },
  Silver: { bg: "from-slate-400 to-slate-600", chip: "bg-slate-100 text-slate-800" },
  Gold: { bg: "from-amber-500 to-orange-600", chip: "bg-amber-50 text-amber-800" },
  Platinum: { bg: "from-blue-800 to-emerald-700", chip: "bg-blue-50 text-blue-800" },
};

const EARN_WAYS = [
  { icon: Sparkles, title: "Book any hotel", desc: "Earn 1 point for every ₹10 spent on bookings." },
  { icon: Gift, title: "Refer a friend", desc: "Get 100 bonus points when they book their first stay." },
  { icon: Award, title: "Post a review", desc: "50 points for every verified review with a comment." },
  { icon: TrendingUp, title: "Move up a tier", desc: "Higher tiers unlock free breakfast, late checkout & more." },
];

export default function Rewards() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rewards").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="p-10"><span className="hw-pulse-dot" /></div>;

  const tier = data.tier;
  const colors = TIER_COLORS[tier.name] || TIER_COLORS.Explorer;
  const nextThreshold = tier.to_next + data.points;
  const progress = nextThreshold ? Math.min(100, Math.round((data.points / nextThreshold) * 100)) : 100;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Loyalty</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Rewards</h1>
        <p className="text-slate-600 mt-2">Earn points on every stay — redeem for wallet credit at checkout.</p>

        {/* Tier card */}
        <div className={`mt-8 relative overflow-hidden rounded-md text-white p-6 hw-shadow-pop bg-gradient-to-br ${colors.bg}`} data-testid="rewards-tier-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="hw-label text-white/80">Current tier</div>
              <div className="hw-head text-4xl font-black mt-1 flex items-center gap-2" data-testid="rewards-tier-name">
                <Award className="w-8 h-8" /> {tier.name}
              </div>
            </div>
            <div className="text-right">
              <div className="hw-label text-white/80">Points</div>
              <div className="hw-head text-4xl font-black mt-1" data-testid="rewards-points">{data.points.toLocaleString("en-IN")}</div>
            </div>
          </div>
          {tier.next && (
            <div className="mt-5">
              <div className="text-xs text-white/90">
                <span data-testid="rewards-to-next">{tier.to_next.toLocaleString("en-IN")}</span> points to reach <span className="font-bold">{tier.next}</span>
              </div>
              <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white/90 rounded-full transition-all" style={{ width: `${progress}%` }} data-testid="rewards-progress-bar" />
              </div>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            {tier.perks.map((p) => (
              <span key={p} className="text-xs bg-white/15 backdrop-blur px-2.5 py-1 rounded-sm border border-white/20">{p}</span>
            ))}
          </div>
        </div>

        {/* How to earn */}
        <div className="mt-8">
          <h2 className="hw-head text-2xl font-black text-slate-900">Earn faster</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="rewards-earn-ways">
            {EARN_WAYS.map((w) => (
              <div key={w.title} className="hw-card p-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-md grid place-items-center flex-shrink-0"><w.icon className="w-5 h-5 text-blue-700" /></div>
                <div>
                  <div className="hw-head font-bold text-slate-900">{w.title}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{w.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/referrals" className="mt-4 inline-flex items-center gap-1.5 text-blue-700 font-bold text-sm hover:text-blue-800" data-testid="rewards-refer-link">
            <Gift className="w-4 h-4" /> Go to Refer & Earn →
          </Link>
        </div>

        {/* History */}
        <div className="mt-8">
          <h2 className="hw-head text-2xl font-black text-slate-900">Points history</h2>
          {data.txns.length === 0 ? (
            <div className="hw-card p-8 mt-4 text-center" data-testid="rewards-history-empty">
              <p className="text-slate-600">No points yet — book a stay to start earning.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="rewards-history-list">
              {data.txns.map((t) => {
                const credit = t.points > 0;
                return (
                  <div key={t.reward_id} className="hw-card p-4 flex items-center gap-4" data-testid={`reward-txn-${t.reward_id}`}>
                    <div className="w-10 h-10 rounded-full bg-blue-50 grid place-items-center flex-shrink-0"><Sparkles className="w-5 h-5 text-blue-700" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate capitalize">{t.kind.replace("_", " ")}</div>
                      <div className="text-xs text-slate-500 truncate">{t.note}</div>
                    </div>
                    <div className="text-right">
                      <div className={`hw-head font-black ${credit ? "text-emerald-700" : "text-red-700"}`}>{credit ? "+" : ""}{t.points.toLocaleString("en-IN")} pts</div>
                      <div className="text-[11px] text-slate-400">{new Date(t.created_at).toLocaleDateString("en-IN")}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
